<template>
    <div>
        <div v-if="updated" class="alert alert-success" role="alert">
            <div class="row">
                <div class="col-1" style="max-width: 30px"><i class="far fa-check"></i></div>
                <div class="col" v-html="updated"></div>
            </div>
        </div>

        <form v-else v-on:submit.prevent="onSubmit()">
            <div v-if="font_errors" v-html="font_errors" class="alert alert-danger" role="alert"></div>

            <div v-if="errors" v-html="errors" class="alert alert-danger form-errors" role="alert"></div>

            <div class="form-group">
                <vue-dropzone
                    v-if="dropzone_options"
                    ref="replaceTemplateDropzone"
                    id="replace-template-dropzone"
                    :options="dropzone_options"
                    v-on:vdropzone-sending="onSendingFile"
                    v-on:vdropzone-upload-progress="onProgressUpdate"
                    v-on:vdropzone-success="onFileUploaded"
                    v-on:vdropzone-error="onUploadError"
                    v-on:vdropzone-drag-over="onDragOver"
                    v-on:vdropzone-drag-leave="onDragLeave"
                    v-on:vdropzone-drop="onDragLeave"
                    v-on:vdropzone-file-added="onFileAdded"
                    :useCustomSlot=true
                >
                    <div v-if="file" class="dz-uploaded-file p-2 mb-1">
                        <div class="dz-thumbnail">
                            <span v-if="!file.preview_image">
                                <img
                                    :src="$root.imagePlaceholder(file.filename)"
                                    class="img-fluid"
                                    alt=""
                                >
                            </span>
                            <img
                                v-else
                                :src="file.preview_image"
                                class="img-fluid"
                                alt=""
                                v-on:mouseover="viewImageIcon(file.id, true)"
                                v-on:mouseout="viewImageIcon(file.id, false)"
                            >
                            <div
                                v-if="file.preview_image"
                                class="view-image-icon"
                                :id="'view-image-icon' + file.id"
                                v-b-tooltip.hover
                                :title="$root.text('Enlarge', null, true)"
                                @click="viewImage(file)"
                                v-on:mouseover="viewImageIcon(file.id, true)"
                                v-on:mouseout="viewImageIcon(file.id, false)"
                            >
                                <i class="fal fa-expand-arrows"></i>
                            </div>
                        </div>
                        <div :class="('dz-filename' + xlgClass())">{{ file.filename }}</div>
                        <div class="dz-size" v-html="$root.formatFileSize(file.file_size)"></div>
                        <div class="dz-remove">
                            <a
                                v-if="!updating"
                                href="#"
                                v-b-tooltip.hover
                                :title="$root.text('Delete', null, true)"
                                @click.prevent="removeUploadedFile()"
                            >
                                <i class="fal fa-trash-alt"></i>
                            </a>
                            <span v-if="updating"><i class="fal fa-trash-alt"></i></span>
                        </div>
                        <div
                            :class="('dz-upload-info' + xlgClass())"
                            :id="'upload-info' + file.id"
                        >
                            <!--<i class="fal fa-clock mr-1"></i> Pending upload-->
                        </div>
                        <div
                            :class="('dz-progress' + xlgClass())"
                            :id="'upload-progress' + file.id"
                        >
                            <div class="dz-upload"></div>
                        </div>
                    </div>
                    <a
                        href="#"
                        class="dz-add-files px-3"
                        id="replace-template-add-files"
                        @click.prevent="doNothing()"
                        :style="dzAddFilesStyle()"
                    >
                        <i class="far fa-cloud-upload-alt font-size-20 mr-1"></i>
                        <span v-if="$root.client.template_from_pdf && !$root.client.template_from_idml">
                            {{ $root.text(
                                'Click or drop file here to upload. Must be a PDF document containing '
                                    + 'editable fields.',
                                null,
                                true
                            ) }}
                        </span>
                        <span v-if="!$root.client.template_from_pdf && $root.client.template_from_idml">
                            {{ $root.text(
                                'Click or drop file here to upload. Must be an IDML (InDesign) file.',
                                null,
                                true
                            ) }}
                        </span>
                        <span v-if="$root.client.template_from_pdf && $root.client.template_from_idml">
                            {{ $root.text(
                                'Click or drop file here to upload. Must be an IDML (InDesign) file or '
                                    + 'a PDF document containing editable fields.',
                                null,
                                true
                            ) }}
                        </span>
                    </a>
                </vue-dropzone>
            </div>
            <div class="form-group">
                <span v-if="isIdmlImported()">
                    <button
                        v-if="!editing_template_scene"
                        :key="'next-button'"
                        type="submit"
                        class="btn btn-primary mr-2"
                        :disabled="errors"
                    >
                        <i class="far fa-arrow-circle-right mr-1"></i> {{ $root.text('Next', null, true) }}
                    </button>
                    <span v-else>
                        <button :key="'next-spinner'" type="button" class="btn btn-primary mr-2">
                            <i class="fal fa-spinner-third fa-spin mr-1"></i>
                            <span style="opacity: 0.5">{{ $root.text('Next', null, true) }}</span>
                        </button>
                    </span>
                </span>
                <span v-if="!isIdmlImported() && processed">
                    <button
                        v-if="!updating"
                        :key="'replace-button'"
                        type="submit"
                        class="btn btn-primary mr-2"
                        :disabled="errors"
                    >
                        <i class="far fa-retweet-alt mr-1"></i> {{ $root.text('Replace', null, true) }}
                    </button>
                    <span v-else>
                        <button :key="'replace-spinner'" type="button" class="btn btn-primary mr-2">
                            <i class="fal fa-spinner-third fa-spin mr-1"></i>
                            <span style="opacity: 0.5">{{ $root.text('Replace', null, true) }}</span>
                        </button>
                    </span>
                </span>
                <button
                    :key="'cancel-button'"
                    type="button"
                    class="btn btn-light"
                    @click="onCancel()"
                    :disabled="(uploading || updating)"
                >
                    <i class="far fa-times mr-1"></i> {{ $root.text('Cancel', null, true) }}
                </button>
            </div>
        </form>
    </div>
</template>

<script>
    import vue2Dropzone from 'vue2-dropzone';
    import CreativeEngine from "@cesdk/engine";
    import { IDMLParser, addGoogleFontsAssetLibrary } from "@imgly/idml-importer";
    export default {
        props: ['data', 'template'],
        components: {
            'vue-dropzone': vue2Dropzone,
            CreativeEngine,
            IDMLParser,
            addGoogleFontsAssetLibrary
        },
        data () {
            return {
                errors: false,
                font_errors: false,
                uploading: false,
                updating: false,
                updated: false,
                dropzone_options: null,
                file: null,
                processed: false,
                template_fields: [],
                template_crop_marks: false,
                template_editable: false,
                template_img_ly_scene: null,
                img_ly_scene_data: null,
                editing_template_scene: false
            }
        },
        mounted () {
            let acceptedFiles = [];
            if (this.$root.client.template_from_pdf) {
                acceptedFiles.push('application/pdf');
            }
            if (this.$root.client.template_from_idml) {
                acceptedFiles.push('.idml');
            }
            this.dropzone_options = {
                url: this.$root.app.api_url + '/' + this.$root.app.client_id + '/upload-v2',
                acceptedFiles: acceptedFiles.join(','),
                createImageThumbnails: true,
                maxThumbnailFilesize: 20,
                thumbnailWidth: 50,
                thumbnailHeight: 50,
                uploadMultiple: false,
                parallelUploads: 1,
                chunking: true,
                forceChunking: true,
                parallelChunkUploads: false,
                chunkSize: 10000000,
                retryChunks: true,
                retryChunksLimit: 5,
                maxFilesize: (100 * 1000 * 1000),
                autoProcessQueue: false,
                timeout: 0,
                clickable: '#replace-template-add-files',
                headers: {
                    'Authorization': 'Bearer ' + this.$root.token
                }
            };
        },
        watch: {
            //
        },
        methods: {
            onSubmit: function () {
                this.errors = false;
                var vm = this;

                if (this.isIdmlImported()) {
                    this.editing_template_scene = true;

                    setTimeout(function () {
                        const scene = {
                            title: vm.$parent.asset.title,
                            ready_for_tagging: false,
                            finished: false
                        };
                        const appComponent = vm.$root.findComponent(vm.$root, 'main-app');
                        appComponent.showImgLySceneEditor(vm.img_ly_scene_data, scene, false, true, vm.$parent);
                    }, 500);
                    
                    return null;
                }

                this.updating = true;
                if (!this.processed) {
                    this.errors = this.$root.text(
                        '{template} not processed.',
                        { template: this.$root.text(this.$root.contents.template_title, null, false, 'template_title') },
                        true
                    );

                    return null;
                }

                let data = this.file ? {
                    temp_file: this.file.temp_file,
                    filename: this.file.filename,
                    file_type: this.file.file_type,
                    file_size: this.file.file_size
                } : {};
                if (this.$root.isFeatured('proofs')) {
                    data.app_info = {
                        app_url: this.$root.app.app_url
                    };
                }
                if (this.template_img_ly_scene) {
                    data.img_ly_scene_id = this.template_img_ly_scene.id;
                }

                /**
                 * Send request to API.
                 */
                const url = this.$root.app.api_url + '/' + this.$root.app.client_id + '/resource/'
                    + this.data.id + '/replace';

                axios({
                    method: 'put',
                    url: url,
                    data: data,
                    headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                })
                .then(response => {
                    vm.replaceTemplate();
                })
                .catch(function (error) {
                    vm.updating = false;
                    if (error.response) {
                        if (error.response.status == 401) {
                            // missing/lost API token - close modal and logout user
                            vm.$root.doLogout();
                        }
                        vm.errors = error.response.data.error
                            || vm.$root.text('Something went wrong, please try again', null, true);
                    }
                })
                .finally(() => {
                    if ($('#message-modal').hasClass('show')) {
                        $('#message-modal').modal('hide');
                    }
                });
            },
            replaceTemplate: function () {
                let fields = [];
                if (this.template_fields.length > 0) {
                    if (this.template_fields[0].number_of_fields > 0) {
                        this.template_fields.forEach((pageFields, pageNum) => {
                            if (pageNum > 0) {
                                pageFields.forEach((field, sortOrder) => {
                                    let fieldData = {
                                        type: field.type,
                                        title: field.title,
                                        page_number: pageNum,
                                        sort_order: sortOrder
                                    };
                                    if (field.type == 'T') {
                                        fieldData.styles = field.styles;
                                        fieldData.repeatable = field.repeatable;
                                        fieldData.group_id = field.group_id;
                                    } else {
                                        fieldData.profile_images = field.profile_images || false;
                                        fieldData.user_images = field.user_images || false;
                                        fieldData.image_editing = field.image_editing || false;
                                        fieldData.system_searching = field.system_searching || false;
                                        if (field.group_id) {
                                            fieldData.group_id = field.group_id;
                                        }
                                    }
                                    fields.push(fieldData);
                                });
                            }
                        });
                    }
                }

                /**
                 * Send request to API.
                 */
                let data = {
                    file_id: this.file ? this.file.id : this.template_img_ly_scene.id,
                    crop_marks: this.template_crop_marks,
                    editable: this.template_editable,
                    fields: fields,
                    fields_managed: !this.hasMultipleTemplateFields(),
                    images_managed: !this.hasTemplateImageFields()
                };
                if (this.template_img_ly_scene) {
                    data.img_ly_scene_id = this.template_img_ly_scene.id;
                }
                const url = this.$root.app.api_url + '/' + this.$root.app.client_id + '/template/'
                    + this.template.id + '/replace';
                var vm = this;

                axios({
                    method: 'put',
                    url: url,
                    data: data,
                    headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                })
                .then(response => {
                    vm.updated = vm.$root.text(
                        '{template} replaced',
                        { template: vm.$root.text(vm.$root.contents.template_title, null, false, 'template_title') },
                        true
                    );

                    // Close after 2 seconds.
                    setTimeout(function () {
                        vm.onCancel();
                        // Reload assets.
                        vm.resetAsset();

                    }, 2000);
                })
                .catch(function (error) {
                    if (error.response) {
                        if (error.response.status == 401) {
                            // missing/lost API token - close modal and logout user
                            vm.$root.doLogout();
                        }
                        vm.errors = error.response.data.error
                            || vm.$root.text('Something went wrong, please try again', null, true);
                    }
                })
                .finally(() => {
                    vm.updating = false;
                    if ($('#message-modal').hasClass('show')) {
                        $('#message-modal').modal('hide');
                    }
                });
            },
            onCancel: function () {
                this.file = null;
                this.errors = false,
                this.font_errors = false,
                this.uploading = false,
                this.updating = false,
                this.updated = false,
                this.processed = false,
                this.template_crop_marks = false,
                this.template_editable = false,
                this.template_img_ly_scene = null;
                this.img_ly_scene_data = null;
                this.editing_template_scene = false;
                this.$parent.replacing = false;
            },
            viewImage: function (file) {
                this.viewImageIcon(file.id, false);

                const appComponent = this.$root.findComponent(this.$root, 'main-app');
                appComponent.message = {
                    type: null,
                    title: file.filename,
                    text: '<div class="text-center"><img src="' + file.preview_image
                        + '" alt="" class="img-fluid" style="max-height: 500px"></div>'
                };
                $('#message-modal > div').addClass('modal-lg');
                $('#message-modal').modal('show');
            },
            viewImageIcon: function (fileId, show) {
                if (show) {
                    $('#view-image-icon' + fileId).show();
                } else {
                    $('#view-image-icon' + fileId).hide();
                }
            },
            doNothing: function () {
                return false;
            },
            dzAddFilesStyle: function () {
                if (!this.file) {
                    return 'display: block';
                }

                return 'display: none';
            },
            onFileAdded: function (file) {
                this.errors = false;
                if (this.file) {
                    this.$refs.replaceTemplateDropzone.removeFile(file);

                    return null;
                }

                // Check file size.
                if (file.size > this.dropzone_options.maxFilesize) {
                    this.$refs.replaceTemplateDropzone.removeFile(file);
                    const appComponent = this.$root.findComponent(this.$root, 'main-app');
                    appComponent.message = {
                        type: 'error',
                        title: this.$root.text('Maximum upload size exceeded', null, true),
                        text: this.$root.text('The maximum size of the file you can upload is {size}.',
                            { size: this.$root.formatFileSize(this.dropzone_options.maxFilesize) }, true) + ' '
                            + this.$root.text('The size of {filename} is {size}.',
                            { filename: file.name, size: this.$root.formatFileSize(file.size) }, true)
                    };
                    $('#message-modal').modal('show');

                    return null;
                }

                let newFile = {
                    id: file.upload.uuid,
                    filename: file.name,
                    file_type: file.type,
                    file_size: file.size,
                    upload: {
                        progress: 0,
                        bytes_sent: 0
                    },
                    preview_image: (file.dataURL || null)
                };
                this.file = newFile;
                var vm = this;
                setTimeout(function () {
                    if (vm.file) {
                        vm.$refs.replaceTemplateDropzone.processQueue();
                        vm.uploading = true;
                        $('#upload-info' + vm.file.id).html(vm.$root.text('Preparing upload...', null, true));
                    }
                }, 500);
            },
            onDragOver: function (event) {
                $('#replace-template-dropzone').addClass('dz-drag-highlight');
            },
            onDragLeave: function (event) {
                $('#replace-template-dropzone').removeClass('dz-drag-highlight');
            },
            onSendingFile: function (file, xhr, formData) {
                formData.append('id', file.upload.uuid);
            },
            onProgressUpdate: function (file, progress, bytesSent) {
                if (!this.file) {
                    return null;
                }
                if ((progress < 100) && (this.file.id == file.upload.uuid)) {
                    if (isNaN(bytesSent)) {
                        bytesSent = file.size * (progress / 100);
                    }
                    this.file.upload.progress = progress;
                    this.file.upload.bytes_sent = bytesSent;
                    $('#upload-info' + this.file.id).html(
                        this.$root.text('Uploading', null, true) + ' ' + this.$root.formatFileSize(bytesSent) + ' '
                            + progress.toFixed(1) + '%'
                    );
                    $('#upload-progress' + this.file.id + ' > div').css('width', progress + '%');
                }
            },
            onFileUploaded: function (file, response) {
                if (!this.file) {
                    return null;
                }
                if (this.file.id == file.upload.uuid) {
                    this.file.upload.progress = 100;
                    this.file.upload.bytes_sent = file.size;
                    $('#upload-progress' + this.file.id + ' > div').css('width', '100%');
                    if (!this.file.preview_image) {
                        this.file.preview_image = (file.dataURL || null);
                    }
                    const isPdf = (this.file.file_type == 'application/pdf');
                    $('#upload-info' + this.file.id).html(
                        this.$root.text(
                            (!isPdf || this.file.preview_image)
                                ? 'Finalising upload...'
                                : 'Previewing...',
                            null,
                            true
                        )
                    );

                    this.$refs.replaceTemplateDropzone.removeFile(file);
                    this.getUploadInfo(this.file.id, isPdf);
                    
                }
            },
            getUploadInfo: function (fileId, isPdf = true) {
                /**
                 * Send request to API.
                 */
                let url = this.$root.app.api_url + '/' + this.$root.app.client_id + '/upload-info/' + fileId;
                if (isPdf) {
                    url += '/preview/500x500';
                }
                var vm = this;

                axios({
                    url: url,
                    headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                })
                .then(response => {
                    if (vm.file.id != response.data.file_id) {
                        return null;
                    }
                    vm.file.temp_file = response.data.temp_file;
                    if (response.data.preview) {
                        vm.file.preview_image = response.data.preview.src;
                    }

                    if (isPdf) {
                        $('#upload-info' + vm.file.id).html('');
                        vm.onUploadComplete();
                        vm.processTemplate();

                        return null;
                    }
                    
                    vm.downloadIdml(vm.file);
                })
                .catch(function (error) {
                    vm.errors = error.response.data.error
                        || vm.$root.text('Something went wrong, pleaase try again.', null, true);
                    $('#upload-info' + vm.file.id).hide();
                });
            },
            downloadIdml: function (file) {
                $('#upload-info' + file.id).html(this.$root.text('Importing IDML...', null, true));
                /**
                 * Send request to API.
                 */
                const data = {
                    item_type: 'temp_file',
                    item_id: 0,
                    file_path: file.temp_file,
                    file_type: 'application/vnd.adobe.indesign-idml-package',
                    download_filename: 'scene.idml'
                };
                const url = this.$root.app.api_url + '/' + this.$root.app.client_id + '/request-download-token';
                var vm = this;

                axios({
                    method: 'post',
                    url: url,
                    data: data,
                    headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                })
                .then(response => {
                    const url = vm.$root.app.api_url + '/' + vm.$root.app.client_id + '/download/'
                        + response.data.token;
                    vm.getTemplateFonts(url, file);
                })
                .catch(function (error) {
                    if (error.response.status == 401) {
                        // Missing/lost API token - logout user.
                        vm.$root.doLogout();
                    }
                    vm.errors = error.response.data.error
                        || vm.$root.text('Something went wrong, please try again', null, true);
                });
            },
            getTemplateFonts: function (idmlUrl, file) {
                /**
                 * Send get template fonts request to API.
                 */
                const url = this.$root.app.api_url + '/' + this.$root.app.client_id + '/template-fonts';
                var vm = this;

                axios({
                    url: url,
                    headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                })
                .then(response => {
                    vm.importIdml(idmlUrl, file, response.data.fonts);
                })
                .catch(function (error) {
                    //
                });
            },
            async importIdml(url, file, templateFonts) {
                const blob = await fetch(url).then((res) => res.blob());

                const engine = await CreativeEngine.init({
                    license: this.$root.app.img_ly_license_key
                });

                // We use google fonts to replace well known fonts in the default font resolver.
                //await addGoogleFontsAssetLibrary(engine);

                // Add template fonts.
                await this.addTemplateFontsToEngine(engine, templateFonts);

                const customFontResolver = async (fontParams, engine) => {
                    const { family, style, weight } = fontParams;

                    // Map InDesign font names to your available fonts.
                    const fontMappings = {
                        'Albert Sans': 'Albert Sans',
                        'AlbertSans-Medium': 'Albert Sans'
                    };

                    const targetFamily = fontMappings[family] || family;

                    const result = await engine.asset.findAssets('template-fonts', {
                        page: 0,
                        perPage: 100
                    });

                    if (result.assets.length === 0) {
                        console.log('No template-fonts asset found');

                        return null;
                    }

                    let typeface = null;
                    result.assets.forEach((asset) => {
                        let typefaceFound = asset.payload?.typeface;
                        if (typefaceFound && (typefaceFound.name.toLowerCase() == targetFamily.toLowerCase())) {
                            typeface = typefaceFound;
                        }
                    });
                    if (typeface === null) {
                        console.log('Asset not found for font family:', targetFamily);
                        this.addFontError(targetFamily, weight, style);

                        return null;
                    }

                    let matchWeights = [weight];
                    if (weight == 'normal') {
                        matchWeights.push('regular');
                    }
                    if (weight == 'regular') {
                        matchWeights.push('normal');
                    }
                    const matchingFont = typeface.fonts.find(
                        f => ((matchWeights.indexOf(f.weight) > -1) && (f.style == style))
                    );
                    if (!matchingFont) {
                        console.log(
                            'Font not found in typeface:',
                            targetFamily,
                            'weight:',
                            matchWeights,
                            'style:',
                            style,
                            'typeface:',
                            typeface
                        );
                        this.addFontError(targetFamily, weight, style);

                        return null;
                    }

                    return { typeface: typeface, font: matchingFont };
                };

                const parser = await IDMLParser.fromFile(
                    engine,
                    blob,
                    (content) => new DOMParser().parseFromString(content, "text/xml"),
                    customFontResolver
                );

                await parser.parse();

                var vm = this;
                engine.scene.saveToString(['blob', 'bundle', 'file', 'http', 'https', 'opfs', 'data', 'buffer'])
                .then(response => {
                    vm.img_ly_scene_data = response;
                    const pages = engine.scene.getPages();
                    if (pages.length > 0) {
                        vm.exportScenePreview(pages[0], engine, file);

                        return null;
                    }
                    $('#upload-info' + file.id).hide();
                    vm.onUploadComplete();
                })
                .catch(function (error) {
                    vm.errors = error;// vm.$root.text('Something went wrong, please try again', null, true);
                    engine.dispose();
                });
            },
            addFontError: function (fontName, weight, style) {
                let fontStyles = (weight == 'normal') ? 'Regular' : this.$root.ucfirst(weight);
                if (style && (style != 'normal')) {
                    fontStyles += ' ' + this.$root.ucfirst(style);
                }
                let error = '<!-- font errors -->' + this.$root.text(
                    'Font {font_name} not found. A substitute font will be used.',
                    { font_name: '<strong>' + fontName + ' ' + fontStyles + '</strong>' },
                    true
                );
                if (this.font_errors && (this.font_errors.indexOf(error) > -1)) {
                    return null;
                }

                if (this.font_errors) {
                    this.font_errors += '<br>';
                } else {
                    this.font_errors = '';
                }
                this.font_errors += error;
            },
            async addTemplateFontsToEngine(engine, templateFonts) {
                const sourceId = 'template-fonts';
                let fontsAdded = [];
                engine.asset.addLocalSource(sourceId);
                //console.log('Added local source to engine:', sourceId);

                templateFonts.forEach((font) => {
                    if (font.files.length > 0) {
                        let fonts = [];
                        font.files.forEach((file) => {
                            let weight = file.weight;
                            if (weight == 'regular') {
                                weight = 'normal';
                            }
                            if (weight == 'black') {
                                weight = 'heavy';
                            }
                            fonts.push({
                                uri: `${window.location.protocol}//${window.location.host}/fonts/client/`
                                    + file.filename,
                                subFamily: this.$root.ucfirst(file.weight)
                                    + (file.style == 'normal' ? '' : (' ' + this.$root.ucfirst(file.style))),
                                weight: weight,
                                style: file.style
                            });
                            fontsAdded.push(font.name + '|' + file.weight + '|' + file.style);
                        });

                        engine.asset.addAssetToSource(sourceId, {
                            id: 'font' + font.id,
                            label: {
                                en: font.name
                            },
                            payload: {
                                typeface: {
                                    name: font.name,
                                    fonts: fonts
                                }
                            }
                        });
                        //console.log('Added font to source:', font.name);
                    }
                });

                // Add IMG.LY default fonts.
                this.$root.img_ly_default_fonts.forEach((font) => {
                    let fonts = [];
                    font.files.forEach((file) => {
                        if (fontsAdded.indexOf(font.name + '|' + file.weight + '|' + file.style) < 0) {
                            let weight = file.weight;
                            if (weight == 'regular') {
                                weight = 'normal';
                            }
                            if (weight == 'black') {
                                weight = 'heavy';
                            }
                            fonts.push({
                                uri: `${window.location.protocol}//${window.location.host}/img-ly-assets/`
                                    + 'ly.img.typeface/fonts/' + font.folder + '/' + file.filename,
                                subFamily: file.subfamily,
                                weight: weight,
                                style: file.style
                            });
                        }
                    });
                    if (fonts.length > 0) {
                        engine.asset.addAssetToSource(sourceId, {
                            id: font.id,
                            label: {
                                en: font.name
                            },
                            payload: {
                                typeface: {
                                    name: font.name,
                                    fonts: fonts
                                }
                            }
                        });
                        //console.log('Added font to source:', font.name);
                    }
                });
            },
            exportScenePreview: async function (blockId, engine, file) {
                const options = {
                    mimeType: 'image/png',
                    pngCompressionLevel: 5/*,
                    targetWidth: 1100,
                    targetHeight: 1100*/
                }

                try {
                    const blob = await engine.block.export(
                        blockId,
                        options
                    );

                    const blobToBase64 = async blob => {
                        return new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = () => resolve(reader.result)
                            reader.onerror = err => reject(err)
                            reader.readAsDataURL(blob)
                        })
                    }

                    const imageData = await blobToBase64(blob);
                    file.preview_image = imageData;
                } catch (error) {
                    console.log(error);
                }
                
                this.processed = false;
                this.template_editable = false;
                $('#upload-info' + file.id).hide();
                this.onUploadComplete();

                engine.dispose();
            },
            processTemplate: function () {
                $('#upload-info' + this.file.id).html(this.$root.text('Detecting editable fields...', null, true));
                /**
                 * Send request to API
                 */
                const data = {
                    id: this.file.id,
                    file: this.file.temp_file
                };
                const url = this.$root.app.api_url + '/' + this.$root.app.client_id + '/template/process';
                var vm = this;

                axios({
                    method: 'post',
                    url: url,
                    data: data,
                    headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                })
                .then(response => {
                    if (vm.file.id == response.data.file_id) {
                        $('#upload-info' + vm.file.id).hide();
                        if (!response.data.editable) {
                            vm.errors = vm.$root.text('This file does not contain any editable field.', null, true);

                            return null;
                        }
                        vm.template_fields = response.data.fields;
                        let hasErrors = false;
                        vm.template_fields.forEach((fields, page) => {
                            if (page > 0) {
                                fields.forEach((field) => {
                                    if ((hasErrors === false) && field.error) {
                                        hasErrors = page;
                                    }
                                    if (field.error) {
                                        hasErrors = true;
                                        if (!vm.errors) {
                                            vm.errors = '';
                                        }
                                        vm.errors += field.error + ' ' + vm.$root.text(
                                            '(Page {x}, Field title: {field_title})',
                                            { x: page, field_title: field.title }
                                        ) + '<br>';
                                    }
                                });
                            }
                        });
                        if (hasErrors) {
                            return null;
                        }
                        vm.template_crop_marks = response.data.crop_marks || false;
                        vm.template_editable = true;
                        vm.processed = true;
                    }
                })
                .catch(function (error) {
                    vm.errors = error.response.data.error
                        || vm.$root.text('Something went wrong, pleaase try again.', null, true);
                    vm.file = null;
                })
                .finally(() => {
                    //
                });
            },
            onUploadComplete: function () {
                this.uploading = false;
            },
            onUploadError: function (file, message, xhr) {
                if (file.status != 'canceled') {
                    $('#upload-info' + file.upload.uuid).hide();
                    let error = null;
                    if (!file.accepted) {
                        error = this.$root.text('Invalid file type: {filename}', { filename: file.name }, true)
                            + '<br><br>' + this.$root.text('Please choose a PDF document.', null, true);
                    }
                    if (!error) {
                        error = this.$root.text('Something went wrong, please try again', null, true);
                    }
                    this.errors = error;
                }
                this.uploading = false;
                console.log(file, message);
            },
            removeUploadedFile: function(confirmed = false) {
                if (confirmed) {
                    const removeUploadFileId = (this.file.upload.progress < 100) ? this.file.id : null;

                    this.file = null;
                    this.errors = false;
                    this.processed = false;

                    if (removeUploadFileId) {
                        this.$refs.replaceTemplateDropzone.getUploadingFiles().forEach((file) => {
                            if (file.upload.uuid == removeUploadFileId) {
                                this.$refs.replaceTemplateDropzone.removeFile(file);
                            }
                        });
                        this.$refs.replaceTemplateDropzone.getQueuedFiles().forEach((file) => {
                            if (file.upload.uuid == removeUploadFileId) {
                                this.$refs.replaceTemplateDropzone.removeFile(file);
                            }
                        });
                    }
                
                    return null;
                }

                this.$parent.$parent.$parent.confirm_delete = {
                    title: this.$root.text('Delete file', null, true),
                    text: this.$root.text(
                        'Are you sure you want to delete: {filename}?',
                        { filename: this.file.filename },
                        true
                    ),
                    component: this,
                    action: 'delete-file'
                };
                $('#confirm-delete-modal').modal('show');
            },
            confirmDelete: function (action) {
                if (action == 'delete-file') {
                    this.removeUploadedFile(true);
                }
            },
            resetAsset: function () {
                /**
                 * Send request to API
                 */
                const url = this.$root.app.api_url + '/' + this.$root.app.client_id + '/resources/in::'
                    + this.data.id;
                var vm = this;

                axios({
                    url: url,
                    headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                })
                .then(response => {
                    if (response.data.resources.data[0]) {
                        vm.$parent.setAsset(response.data.resources.data[0]);
                        vm.$parent.reload_assets_on_close = true;
                    }
                })
                .catch(function (error) {
                    vm.$parent.reload_assets_on_close = true;
                    vm.$parent.close();
                });
            },
            xlgClass: function () {
                if (this.$root.is_lg_screen && !this.$root.is_xxl_screen) {
                    return ' xlg';
                }

                return '';
            },
            hasMultipleTemplateFields: function () {
                if (!this.template_fields) {
                    return false;
                }
                let numFields = 0;
                this.template_fields.forEach((fields, page) => {
                    if (page > 0) {
                        //numFields += fields.length;
                        fields.forEach((field) => {
                            let suffix = field.title.substr(-3);
                            if ((field.type == 'T') || (suffix != '_Lx')) {
                                numFields++;
                            }
                        });
                    }
                });

                return (numFields > 1);
            },
            hasTemplateImageFields: function () {
                if (!this.template_fields) {
                    return false;
                }
                let numFields = 0;
                this.template_fields.forEach((fields, page) => {
                    if (page > 0) {
                        fields.forEach((field) => {
                            //let title = field.title.substr(0, field.title.length - 3);
                            let suffix = field.title.substr(-3);
                            if (
                                (field.type == 'I')
                                && ((suffix != '_Lx')/* || (this.locked_profile_fields.indexOf('I' + title) < 0)*/)
                            ) {
                                numFields++;
                            }
                        });
                    }
                });

                return (numFields > 0);
            },
            loadImgLyScene: function () {
                this.file = null;
                let scene = {};
                for (const [key, value] of Object.entries(this.template.img_ly_scene)) {
                    scene[key] = value;
                }
                // Jira R2-1017 - if the template scene is already finished reset to allow further editing.
                if (scene.finished) {
                    scene.finished = false;
                    scene.ready_for_tagging = false;
                }
                this.template_img_ly_scene = scene;

                /**
                 * Send get scene request to API.
                 */
                let url = this.$root.app.api_url + '/' + this.$root.app.client_id + '/img-ly-scene/'
                    + this.template_img_ly_scene.id;
                var vm = this;

                axios({
                    url: url,
                    headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                })
                .then(response => {
                    const download = response.data.file;
                    const data = {
                        item_type: 'img_ly_scene',
                        item_id: vm.template_img_ly_scene.id,
                        file_path: download.file_path,
                        file_type: download.file_type,
                        download_filename: download.filename + '.' + download.extension
                    };
                    url = vm.$root.app.api_url + '/' + vm.$root.app.client_id + '/request-download-token';

                    axios({
                        method: 'post',
                        url: url,
                        data: data,
                        headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                    })
                    .then(response => {
                        const downloadUrl = vm.$root.app.api_url + '/' + vm.$root.app.client_id + '/download/'
                            + response.data.token;

                        axios({
                            url: downloadUrl,
                            headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                        })
                        .then(response => {
                            const sceneData = response.data;
                            setTimeout(function () {
                                const appComponent = vm.$root.findComponent(vm.$root, 'main-app');
                                appComponent.showImgLySceneEditor(
                                    sceneData,
                                    vm.template_img_ly_scene,
                                    false,
                                    false,
                                    vm.$parent
                                );
                            }, 200);
                        })
                        .catch(function (error) {
                            console.log('Error downloading scene data:', error);
                            // Show any errors?
                        });
                    })
                    .catch(function (error) {
                        console.log('Error getting download token:', error);
                        // Show any errors?
                    });
                })
                .catch(function (error) {
                    console.log('Error loading ImgLy scene:', error);
                    // Show any errors?
                });
            },
            onImgLySceneUpdated: function (sceneId) {
                // Get scene details.
                var vm = this;
                const url = this.$root.app.api_url + '/' + this.$root.app.client_id + '/img-ly-scene/' + sceneId;

                axios({
                    url: url,
                    headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                })
                .then(response => {
                    const scene = response.data.scene;

                    if (scene.finished && (scene.placeholders !== null)) {
                        if (scene.placeholders.length > 0) {
                            let templateFields = [{ number_of_fields: scene.placeholders.length }];
                            for (let i = 1; i <= scene.number_of_pages; i++) {
                                templateFields.push([]);
                            }
                            scene.placeholders.forEach((placeholder) => {
                                let fieldData = {
                                    type: placeholder.type,
                                    title: placeholder.name,
                                    error: false,
                                    group_id: placeholder.block_id
                                };
                                if (placeholder.type == 'T') {
                                    fieldData.styles = null;
                                    fieldData.repeatable = false;
                                } else if (placeholder.type == 'I') {
                                    fieldData.profile_images = false;
                                    fieldData.user_images = false;
                                    fieldData.image_editing = false;
                                    fieldData.system_searching = false;
                                }
                                templateFields[placeholder.page_number].push(fieldData);
                            });
                            vm.template_fields = templateFields;
                        }
                    }
                    vm.processed = true;
                    vm.template_editable = scene.finished;
                    vm.template_crop_marks = false;
                    vm.template_img_ly_scene = scene;

                    if (vm.editing_template_scene) {
                        vm.editing_template_scene = false;
                        if (!scene.finished) {
                            vm.updateFilePreview();

                            return null;
                        }
                    }

                    vm.onSubmit();
                })
                .catch(function (error) {
                    console.log('Error processing template:', error);
                    // Show any errors?
                });
            },
            isIdmlImported: function () {
                if (!this.file) {
                    return false;
                }
                if ((this.file.file_type == 'application/pdf') || !this.file.temp_file) {
                    return false;
                }
                if (!this.img_ly_scene_data) {
                    return false;
                }
                if (this.processed) {
                    return false;
                }

                return !this.uploading;
            },
            updateFilePreview: function () {
                this.file.preview_image = null;
                /**
                 * Send request to API.
                 */
                const url = this.$root.app.api_url + '/' + this.$root.app.client_id + '/img-ly-scene/'
                    + this.template_img_ly_scene.id + '/preview/mediumres';
                var vm = this;

                axios({
                    url: url,
                    headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                })
                .then(response => {
                    if (response.data.image_src) {
                        vm.file.preview_image = response.data.image_src;
                    }
                })
                .catch(function (error) {
                    // Show any errors?
                });
            }
        }
    }
</script>
<style scoped>
    #replace-template-dropzone {
        min-height: 126px;
    }

    #replace-template-add-files {
        padding-top: 36px;
    }

    .dz-uploaded-file {
        cursor: default;
    }

    .dz-uploaded-file .dz-upload-info {
        left: 160px;
    }

    .dz-uploaded-file .dz-progress {
        width: calc(100% - 172px);
    }

    .dz-uploaded-file .dz-filename.xlg {
        height: 82px;
        max-height: 82px;
    }

    .dz-uploaded-file .dz-upload-info.xlg {
        left: 12px;
    }

    .dz-uploaded-file .dz-progress.xlg {
        width: calc(100% - 24px);
    }
</style>
